import {
    computed,
    defineComponent,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import Canvas, { ChartColors } from "../components/chart.js";
import DashboardDonut from "../components/dashboardDonut.js";
import DashboardHeatmap from "../components/dashboardHeatmap.js";
import LogSearchEditor from "../logSearch/editor/LogSearchEditor.js";
import { getLogSearchDiagnostics } from "../logSearch/editor/diagnostics.js";
import {
    DashboardHeatmapOptions,
    resolveDashboardHeatmapOptions,
    resolveDashboardHeatmapWindowBefore,
} from "./dashboard/heatmap.js";
import {
    DashboardRankingOptions,
    resolveDashboardRankingOptions,
} from "./dashboard/ranking.js";
import {
    fetchMissingEntityLabels,
    getCachedEntityLabel,
} from "./dashboard/query.js";
import { useFetch } from "../utils.js";

type DashboardGraphType = "line" | "bar" | "stacked-bar" | "metric" | "counter" | "heatmap" | "ranking" | "donut";
type BuilderStep = 1 | 2 | 3;

type BuilderDataset = {
    name: string;
    label: string;
    expression: string;
    color: string;
    drilldown: {
        enabled: boolean;
        expression: string;
        groupBy: "executor" | "target" | "channel";
    };
};

type DashboardResponse = {
    label: string;
    graphType?: DashboardGraphType;
    heatmap?: DashboardHeatmapOptions;
    ranking?: DashboardRankingOptions;
    datasets: BuilderDataset[];
};

type DashboardPreviewResponse = {
    error?: string;
    result?: {
        values?: Record<string, number[]>;
    };
};

const BuilderColors = [
    ChartColors.mint,
    ChartColors.coral,
    ChartColors.violet,
    ChartColors.amber,
    ChartColors.cyan,
    ChartColors.pink,
    ChartColors.blue,
    ChartColors.slate,
];

const PreviewPeriods = [
    { label: "30 min", duration: 30 * 60_000, bucket: 60_000 },
    { label: "1 h", duration: 60 * 60_000, bucket: 5 * 60_000 },
    { label: "1 j", duration: 24 * 60 * 60_000, bucket: 60 * 60_000 },
    { label: "7 j", duration: 7 * 24 * 60 * 60_000, bucket: 6 * 60 * 60_000 },
    { label: "30 j", duration: 30 * 24 * 60 * 60_000, bucket: 24 * 60 * 60_000 },
] as const;

const GraphTypeOptions: {
    type: DashboardGraphType;
    icon: string;
    label: string;
    shortLabel: string;
    description: string;
    dataLabel: string;
}[] = [
    {
        type        : "line",
        icon        : "show_chart",
        label       : "Courbe premium",
        shortLabel  : "Courbe",
        description : "Suit une ou plusieurs tendances dans le temps.",
        dataLabel   : "Séries",
    },
    {
        type        : "bar",
        icon        : "bar_chart",
        label       : "Barres",
        shortLabel  : "Barres",
        description : "Compare clairement les volumes entre chaque période.",
        dataLabel   : "Séries",
    },
    {
        type        : "stacked-bar",
        icon        : "stacked_bar_chart",
        label       : "Barres empilées",
        shortLabel  : "Barres empilées",
        description : "Montre le volume total et la part de chaque série.",
        dataLabel   : "Séries",
    },
    {
        type        : "metric",
        icon        : "monitoring",
        label       : "Valeur avec tendance",
        shortLabel  : "Valeur + tendance",
        description : "Affiche un total, son évolution et une mini-courbe.",
        dataLabel   : "Mesures",
    },
    {
        type        : "counter",
        icon        : "looks_one",
        label       : "Compteur simple",
        shortLabel  : "Compteur simple",
        description : "Affiche le dernier point et son évolution récente.",
        dataLabel   : "Mesures",
    },
    {
        type        : "heatmap",
        icon        : "grid_view",
        label       : "Heatmap 4 semaines",
        shortLabel  : "Heatmap",
        description : "Compare une série sur 28 cases quotidiennes.",
        dataLabel   : "Série",
    },
    {
        type        : "ranking",
        icon        : "leaderboard",
        label       : "Top N",
        shortLabel  : "Classement",
        description : "Classe automatiquement les exécuteurs, cibles ou salons.",
        dataLabel   : "Mesure",
    },
    {
        type        : "donut",
        icon        : "donut_large",
        label       : "Donut",
        shortLabel  : "Répartition",
        description : "Compare la part de chaque série dans le total.",
        dataLabel   : "Catégories",
    },
];

const BuilderSteps: { id: BuilderStep; label: string; description: string }[] = [
    { id: 1, label: "Apparence", description: "Nom et type" },
    { id: 2, label: "Données", description: "Expressions" },
    { id: 3, label: "Vérification", description: "Aperçu final" },
];

function createDataset(index: number): BuilderDataset {
    return {
        name        : `series_${index + 1}`,
        label       : `Série ${index + 1}`,
        expression  : "",
        color       : BuilderColors[index % BuilderColors.length],
        drilldown: {
            enabled     : true,
            expression  : "",
            groupBy     : "executor",
        },
    };
}

function buildLabels(length: number, timeRange: number, before: number) {
    const end = Math.ceil(before / timeRange) * timeRange;

    return Array.from({ length }, (_, index) => {
        return new Date(end - (length - index) * timeRange).toLocaleString();
    });
}

function sumValues(values: number[]) {
    return values.reduce((sum, value) => sum + value, 0);
}

function calculatePeriodTrend(values: number[]) {
    const midpoint = Math.max(1, Math.floor(values.length / 2));
    const previous = sumValues(values.slice(0, midpoint));
    const current = sumValues(values.slice(midpoint));
    const delta = current - previous;

    return {
        delta,
        percent: previous === 0 ? (current === 0 ? 0 : 100) : delta / previous * 100,
    };
}

function formatPreviewNumber(value: number) {
    return new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 1,
        notation: value >= 10_000 ? "compact" : "standard",
    }).format(value);
}

function hasEvents(values: Record<string, number[]>) {
    return Object.values(values).some((series) => series.some((value) => value !== 0));
}

export default defineComponent({
    setup() {
        const { fetch }     = useFetch();
        const router        = useRouter();
        const route         = useRoute();
        const dashboardId   = computed(() => typeof route.params["id"] === "string" ? route.params["id"] : null);
        const activeStep    = ref<BuilderStep>(1);
        const label         = ref("Nouveau graphique");
        const graphType     = ref<DashboardGraphType>("line");
        const previewPeriod = ref(60 * 60_000);
        const previewBefore = ref(Date.now());
        const heatmapOptions = ref<DashboardHeatmapOptions>(resolveDashboardHeatmapOptions());
        const rankingOptions = ref<DashboardRankingOptions>(resolveDashboardRankingOptions());
        const rankingLabels = ref(new Map<string, string>());
        const datasets      = ref<BuilderDataset[]>([ createDataset(0) ]);
        const parkedDatasets = ref<BuilderDataset[]>([]);
        const selectedDatasetIndex = ref(0);
        const preview       = ref<DashboardPreviewResponse | null>(null);
        const previewError  = ref<string | null>(null);
        const formError     = ref<string | null>(null);
        const loading       = ref(false);
        const saving        = ref(false);
        let previewTimer: ReturnType<typeof setTimeout> | null = null;
        let previewRequestId = 0;

        const payload = computed(() => ({
            label       : label.value,
            graphType   : graphType.value,
            heatmap     : graphType.value === "heatmap" ? heatmapOptions.value : undefined,
            ranking     : graphType.value === "ranking" ? rankingOptions.value : undefined,
            datasets    : datasets.value,
        }));
        const selectedGraphType = computed(() => (
            GraphTypeOptions.find((option) => option.type === graphType.value) || GraphTypeOptions[0]!
        ));
        const dataLabel = computed(() => selectedGraphType.value.dataLabel);
        const isHeatmap = computed(() => graphType.value === "heatmap");
        const isRanking = computed(() => graphType.value === "ranking");
        const usesSingleSeries = computed(() => isHeatmap.value || isRanking.value);
        const effectivePreviewDuration = computed(() => (
            isHeatmap.value
                ? heatmapOptions.value.bucketMs * heatmapOptions.value.cellCount
                : previewPeriod.value
        ));
        const effectivePreviewBucket = computed(() => {
            if (isHeatmap.value)
                return heatmapOptions.value.bucketMs;

            return PreviewPeriods.find((period) => period.duration === previewPeriod.value)?.bucket || PreviewPeriods[1].bucket;
        });
        const missingExpression = computed(() => datasets.value.some((dataset) => !dataset.expression.trim()));
        const selectedDataset = computed(() => (
            datasets.value[selectedDatasetIndex.value] || datasets.value[0]
        ));
        const configurationError = computed(() => validateConfiguration());
        const canPreview = computed(() => !configurationError.value);

        onMounted(async () => {
            if (!dashboardId.value)
                return;

            try {
                const response = await fetch(`/api/dashboards/${dashboardId.value}`);

                if (response.status !== 200)
                    throw new Error("Ce graphique n’a pas pu être chargé.");

                const dashboard = await response.json() as DashboardResponse;

                label.value     = dashboard.label;
                graphType.value = dashboard.graphType || "line";
                heatmapOptions.value = resolveDashboardHeatmapOptions(dashboard.heatmap);
                rankingOptions.value = resolveDashboardRankingOptions(dashboard.ranking);
                datasets.value  = dashboard.datasets.length ? dashboard.datasets : [ createDataset(0) ];
            } catch (caughtError) {
                formError.value = caughtError instanceof Error ? caughtError.message : "Chargement impossible.";
            }
        });

        onBeforeUnmount(() => {
            if (previewTimer)
                clearTimeout(previewTimer);
        });

        watch(payload, schedulePreview, { deep: true });
        watch(previewPeriod, schedulePreview);

        function validateConfiguration() {
            const trimmedLabel = label.value.trim();

            if (!trimmedLabel || trimmedLabel.length > 80)
                return "Donne un nom au graphique (80 caractères maximum).";

            if (!datasets.value.length)
                return "Ajoute au moins une série.";

            if (usesSingleSeries.value && datasets.value.length !== 1)
                return `${isHeatmap.value ? "La heatmap" : "Le Top N"} doit contenir exactement une série.`;

            if (graphType.value === "donut" && datasets.value.length < 2)
                return "Le donut doit contenir au moins deux catégories.";

            const names = new Set<string>();

            for (let index = 0; index < datasets.value.length; index++) {
                const dataset = datasets.value[index]!;
                const datasetNumber = index + 1;

                if (!dataset.label.trim())
                    return `Donne un nom à la donnée ${datasetNumber}.`;

                if (!dataset.name.trim())
                    return `L’identifiant de la série ${datasetNumber} est vide.`;

                if (names.has(dataset.name.trim()))
                    return `L’identifiant « ${dataset.name.trim()} » est utilisé plusieurs fois.`;

                names.add(dataset.name.trim());

                if (!dataset.expression.trim())
                    return `Ajoute une expression à « ${dataset.label.trim()} ».`;

                if (getLogSearchDiagnostics(dataset.expression).some((diagnostic) => diagnostic.severity === "error"))
                    return `Corrige l’expression de « ${dataset.label.trim()} ».`;
            }

            return null;
        }

        function addDataset() {
            if (usesSingleSeries.value || datasets.value.length >= 8)
                return;

            const usedNames = new Set(datasets.value.map((dataset) => dataset.name));
            let candidateIndex = datasets.value.length;

            while (usedNames.has(`series_${candidateIndex + 1}`))
                candidateIndex++;

            datasets.value.push(createDataset(candidateIndex));
            selectedDatasetIndex.value = datasets.value.length - 1;
        }

        function removeDataset(index: number) {
            if (datasets.value.length <= 1)
                return;

            datasets.value.splice(index, 1);
            selectedDatasetIndex.value = Math.min(selectedDatasetIndex.value, datasets.value.length - 1);
        }

        function selectGraphType(type: DashboardGraphType) {
            if (type === graphType.value)
                return;

            const nextUsesSingleSeries = type === "heatmap" || type === "ranking";
            const currentUsesSingleSeries = graphType.value === "heatmap" || graphType.value === "ranking";

            if (nextUsesSingleSeries && !currentUsesSingleSeries && datasets.value.length > 1) {
                parkedDatasets.value = datasets.value.slice(1);
                datasets.value = [ datasets.value[0]! ];
                selectedDatasetIndex.value = 0;
            } else if (!nextUsesSingleSeries && currentUsesSingleSeries && parkedDatasets.value.length) {
                datasets.value = [ ...datasets.value, ...parkedDatasets.value ];
                parkedDatasets.value = [];
            }

            graphType.value = type;
        }

        function schedulePreview() {
            previewRequestId++;
            preview.value = null;
            previewError.value = null;
            rankingLabels.value = new Map();
            loading.value = false;

            if (previewTimer)
                clearTimeout(previewTimer);

            if (!canPreview.value) {
                loading.value = false;
                return;
            }

            previewTimer = setTimeout(() => void refreshPreview(), 700);
        }

        async function refreshPreview() {
            const validationError = validateConfiguration();

            if (previewTimer) {
                clearTimeout(previewTimer);
                previewTimer = null;
            }

            if (validationError) {
                preview.value = null;
                previewError.value = null;
                return;
            }

            const requestId = ++previewRequestId;
            loading.value = true;
            previewError.value = null;

            try {
                const bucket = effectivePreviewBucket.value;
                const before = isHeatmap.value
                    ? resolveDashboardHeatmapWindowBefore(Date.now(), bucket)
                    : Math.ceil(Date.now() / bucket) * bucket;
                const after = before - effectivePreviewDuration.value;
                previewBefore.value = before;
                const params = new URLSearchParams({
                    timeRange : String(bucket),
                    after     : String(after),
                    before    : String(before),
                });
                const res = await fetch(`/api/dashboard-stats/preview?${params}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload.value),
                });
                const body = await res.json() as DashboardPreviewResponse;

                if (requestId !== previewRequestId)
                    return;

                if (res.status !== 200) {
                    preview.value = null;
                    previewError.value = body.error || "La prévisualisation est impossible.";
                    return;
                }

                if (isRanking.value && body.result?.values) {
                    const ids = Object.keys(body.result.values);
                    const labelKind = rankingOptions.value.groupBy === "channel" ? "channel" : "member";

                    await fetchMissingEntityLabels(fetch, labelKind, ids).catch(() => undefined);

                    if (requestId !== previewRequestId)
                        return;

                    rankingLabels.value = new Map(ids.map((id) => [
                        id,
                        getCachedEntityLabel(labelKind, id) || id,
                    ]));
                }

                preview.value = body;
            } catch {
                if (requestId === previewRequestId)
                    previewError.value = "La prévisualisation est momentanément indisponible.";
            } finally {
                if (requestId === previewRequestId)
                    loading.value = false;
            }
        }

        async function saveDashboard() {
            const validationError = validateConfiguration();

            if (validationError) {
                formError.value = validationError;
                activeStep.value = label.value.trim() ? 2 : 1;
                return;
            }

            saving.value = true;
            formError.value = null;

            try {
                const res = await fetch(dashboardId.value ? `/api/dashboards/${dashboardId.value}` : "/api/dashboards", {
                    method: dashboardId.value ? "PATCH" : "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload.value),
                });
                const body = await res.json().catch(() => null);

                if (![ 200, 201 ].includes(res.status)) {
                    formError.value = body?.error || "La sauvegarde est impossible.";
                    return;
                }

                await router.push("/");
            } finally {
                saving.value = false;
            }
        }

        function goToStep(step: BuilderStep) {
            if (step === 3 && configurationError.value) {
                formError.value = configurationError.value;
                return;
            }

            formError.value = null;
            activeStep.value = step;

            if (step === 3)
                void refreshPreview();
        }

        function goNext() {
            if (activeStep.value === 1) {
                if (!label.value.trim()) {
                    formError.value = "Donne un nom au graphique avant de continuer.";
                    return;
                }

                goToStep(2);
                return;
            }

            if (activeStep.value === 2) {
                const validationError = validateConfiguration();

                if (validationError) {
                    formError.value = validationError;
                    return;
                }

                goToStep(3);
            }
        }

        function renderPreviewMetric(values: Record<string, number[]>) {
            const series = Object.entries(values).map(([ name, data ]) => {
                const dataset = datasets.value.find((entry) => entry.name === name);

                return {
                    label   : dataset?.label || name,
                    color   : dataset?.color || ChartColors.mint,
                    total   : sumValues(data),
                };
            });
            const total = sumValues(series.map((entry) => entry.total));
            const firstSeries = Object.values(values)[0] || [];
            const mergedValues = firstSeries.map((_, index) => (
                Object.values(values).reduce((sum, entry) => sum + (entry[index] || 0), 0)
            ));
            const trend = calculatePeriodTrend(mergedValues);
            const labels = buildLabels(mergedValues.length, effectivePreviewBucket.value, previewBefore.value);

            return (
                <div class="dashboard-builder-metric-preview">
                    <div class="dashboard-builder-metric-copy">
                        <span>Total sur la période</span>
                        <strong>{formatPreviewNumber(total)}</strong>
                        <small class={trend.delta > 0 ? "positive" : trend.delta < 0 ? "negative" : "neutral"}>
                            <i class="material-symbols-outlined">
                                {trend.delta > 0 ? "trending_up" : trend.delta < 0 ? "trending_down" : "trending_flat"}
                            </i>
                            {`${trend.delta >= 0 ? "+" : ""}${formatPreviewNumber(trend.delta)} · ${trend.percent >= 0 ? "+" : ""}${trend.percent.toFixed(1)} %`}
                        </small>
                    </div>
                    <div class="dashboard-builder-metric-sparkline">
                        <Canvas options={{
                            visiblePoints : mergedValues.length || 1,
                            graphType     : "line",
                            labels,
                            datasets: [{
                                label : "Total",
                                data  : mergedValues,
                                color : datasets.value[0]?.color || ChartColors.mint,
                            }],
                        }} />
                    </div>
                    <div class="dashboard-builder-metric-series-preview">
                        {series.map((entry) => (
                            <small>
                                <i style={{ background: entry.color }} />
                                {entry.label}
                                <b>{formatPreviewNumber(entry.total)}</b>
                            </small>
                        ))}
                    </div>
                </div>
            );
        }

        function renderPreviewDonut(values: Record<string, number[]>) {
            return (
                <div class="dashboard-builder-chart-preview">
                    <DashboardDonut entries={Object.entries(values).map(([ name, data ]) => {
                        const dataset = datasets.value.find((entry) => entry.name === name);

                        return {
                            label : dataset?.label || name,
                            value : sumValues(data),
                            color : dataset?.color || ChartColors.mint,
                        };
                    })} />
                </div>
            );
        }

        function renderPreviewRanking(values: Record<string, number[]>) {
            const color = datasets.value[0]?.color || ChartColors.mint;
            const entries = Object.entries(values)
                .map(([ id, data ]) => ({ id, count: sumValues(data) }))
                .sort((left, right) => right.count - left.count)
                .slice(0, rankingOptions.value.limit);
            const maximum = entries[0]?.count || 1;

            return (
                <div class="dashboard-builder-chart-preview">
                    <div class="dashboard-ranking">
                        {entries.map((entry, index) => (
                            <div class="dashboard-ranking-row" key={entry.id}>
                                <span class="dashboard-ranking-position">{index + 1}</span>
                                <div class="dashboard-ranking-value">
                                    <span>{rankingLabels.value.get(entry.id) || entry.id}</span>
                                    <div class="dashboard-ranking-track">
                                        <i style={{
                                            "--ranking-color": color,
                                            background: color,
                                            inlineSize: `${Math.max(4, entry.count / maximum * 100)}%`,
                                        }} />
                                    </div>
                                </div>
                                <strong>{formatPreviewNumber(entry.count)}</strong>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        function renderPreviewCounter(values: Record<string, number[]>) {
            const series = Object.entries(values).map(([ name, data ]) => {
                const dataset = datasets.value.find((entry) => entry.name === name);

                return {
                    label       : dataset?.label || name,
                    color       : dataset?.color || ChartColors.mint,
                    current     : data.at(-1) || 0,
                    previous    : data.at(-2) || 0,
                };
            });
            const current = sumValues(series.map((entry) => entry.current));
            const previous = sumValues(series.map((entry) => entry.previous));
            const delta = current - previous;

            return (
                <div class="dashboard-builder-counter-preview">
                    <span>Dernier point</span>
                    <strong>{formatPreviewNumber(current)}</strong>
                    <small class={delta >= 0 ? "positive" : "negative"}>
                        <i class="material-symbols-outlined">{delta >= 0 ? "trending_up" : "trending_down"}</i>
                        {`${delta >= 0 ? "+" : ""}${formatPreviewNumber(delta)} vs point précédent`}
                    </small>
                    <div>
                        {series.map((entry) => (
                            <small>
                                <i style={{ background: entry.color }} />
                                {entry.label}
                                <b>{formatPreviewNumber(entry.current)}</b>
                            </small>
                        ))}
                    </div>
                </div>
            );
        }

        function renderPreviewPlaceholder(title: string, description: string, icon: string) {
            return (
                <div class={{
                    "dashboard-builder-empty-preview": true,
                    "is-counter": graphType.value === "metric" || graphType.value === "counter",
                }}>
                    {isHeatmap.value ? (
                        <DashboardHeatmap
                            values={Array.from({ length: heatmapOptions.value.cellCount }, () => 0)}
                            color={datasets.value[0]?.color || ChartColors.mint}
                            label={datasets.value[0]?.label || "Série"}
                            before={previewBefore.value}
                            bucketMs={heatmapOptions.value.bucketMs}
                            cellCount={heatmapOptions.value.cellCount}
                            scale={heatmapOptions.value.scale}
                        />
                    ) : (graphType.value === "metric" || graphType.value === "counter") ? (
                        <strong class="dashboard-builder-empty-number">—</strong>
                    ) : (
                        <div class="dashboard-builder-empty-chart" aria-hidden="true">
                            {[ 1, 2, 3, 4 ].map(() => <i />)}
                        </div>
                    )}
                    <div class="dashboard-builder-empty-message">
                        <i class="material-symbols-outlined">{icon}</i>
                        <strong>{title}</strong>
                        <span>{description}</span>
                        {missingExpression.value && (
                            <button type="button" onClick={() => goToStep(2)}>Configurer les données</button>
                        )}
                    </div>
                </div>
            );
        }

        function renderPreview() {
            if (missingExpression.value)
                return renderPreviewPlaceholder(
                    "Ajoute une expression",
                    "La structure est prête. Les données réelles apparaîtront dès que chaque expression sera renseignée.",
                    "data_object",
                );

            if (previewError.value)
                return (
                    <div class="dashboard-builder-preview-feedback error">
                        <i class="material-symbols-outlined">error</i>
                        <strong>Impossible d’afficher les données</strong>
                        <span>{previewError.value}</span>
                        <button type="button" onClick={refreshPreview}>Réessayer</button>
                    </div>
                );

            if (loading.value && !preview.value)
                return (
                    <div class="dashboard-builder-preview-feedback loading">
                        <i class="material-symbols-outlined">progress_activity</i>
                        <strong>Chargement des données réelles…</strong>
                        <span>La requête est exécutée sur la période sélectionnée.</span>
                    </div>
                );

            const values = preview.value?.result?.values;

            if (!values)
                return renderPreviewPlaceholder(
                    "Prévisualisation en préparation",
                    "Termine la configuration des données pour générer le graphique.",
                    "monitoring",
                );

            if (!hasEvents(values))
                return (
                    <div class="dashboard-builder-preview-feedback no-data">
                        <i class="material-symbols-outlined">search_off</i>
                        <strong>Aucun événement trouvé</strong>
                        <span>L’expression est valide, mais elle ne renvoie rien sur la période actuelle.</span>
                        <button type="button" onClick={() => goToStep(2)}>Modifier l’expression</button>
                    </div>
                );

            if (graphType.value === "metric")
                return renderPreviewMetric(values);

            if (graphType.value === "counter")
                return renderPreviewCounter(values);

            if (graphType.value === "donut")
                return renderPreviewDonut(values);

            if (graphType.value === "ranking")
                return renderPreviewRanking(values);

            if (graphType.value === "heatmap") {
                const dataset = datasets.value[0]!;
                const heatmapValues = values[dataset.name] || Object.values(values)[0] || [];

                return (
                    <div class="dashboard-builder-chart-preview">
                        <DashboardHeatmap
                            values={heatmapValues}
                            color={dataset.color}
                            label={dataset.label}
                            before={previewBefore.value}
                            bucketMs={heatmapOptions.value.bucketMs}
                            cellCount={heatmapOptions.value.cellCount}
                            scale={heatmapOptions.value.scale}
                        />
                    </div>
                );
            }

            const seriesValues = Object.values(values) as number[][];
            const labels = buildLabels(seriesValues[0]?.length || 0, effectivePreviewBucket.value, previewBefore.value);

            return (
                <div class="dashboard-builder-chart-preview">
                    <Canvas options={{
                        visiblePoints   : seriesValues[0]?.length || 1,
                        graphType       : [ "bar", "stacked-bar" ].includes(graphType.value) ? "bar" : "line",
                        stacked         : graphType.value === "stacked-bar",
                        labels,
                        datasets: Object.entries(values).map(([ name, data ]) => {
                            const dataset = datasets.value.find((entry) => entry.name === name);

                            return {
                                label   : dataset?.label || name,
                                data    : data as number[],
                                color   : dataset?.color || ChartColors.mint,
                            };
                        }),
                    }} />
                </div>
            );
        }

        function renderAppearanceStep() {
            return (
                <>
                    <div class="dashboard-builder-panel-heading">
                        <div>
                            <span>Étape 1</span>
                            <h2>Nom et type de graphique</h2>
                            <p>Choisis la façon dont cette donnée sera présentée sur le dashboard.</p>
                        </div>
                    </div>

                    <label class="dashboard-builder-field dashboard-builder-name-field">
                        <span>Nom du graphique</span>
                        <input
                            maxlength={80}
                            value={label.value}
                            placeholder="Ex. Messages créés"
                            onInput={(event) => label.value = (event.target as HTMLInputElement).value}
                        />
                        <small>{label.value.length}/80</small>
                    </label>

                    <div class="dashboard-builder-type-grid">
                        {GraphTypeOptions.map((option) => (
                            <button
                                type="button"
                                class={{ "dashboard-builder-type-card": true, active: graphType.value === option.type }}
                                aria-pressed={graphType.value === option.type}
                                onClick={() => selectGraphType(option.type)}
                            >
                                <i class="material-symbols-outlined">{option.icon}</i>
                                <span class="dashboard-builder-type-copy">
                                    <strong>{option.label}</strong>
                                    <small>{option.description}</small>
                                </span>
                                <i class="material-symbols-outlined dashboard-builder-type-check">check_circle</i>
                            </button>
                        ))}
                    </div>
                </>
            );
        }

        function renderDatasetEditor(dataset: BuilderDataset, index: number) {
            const diagnostics = getLogSearchDiagnostics(dataset.expression);

            return (
                <div class="dashboard-builder-dataset-editor">
                    <div class="dashboard-builder-dataset-editor-heading">
                        <div>
                            <span>{dataLabel.value.replace(/s$/, "")} {index + 1}</span>
                            <h3>{dataset.label || "Sans nom"}</h3>
                        </div>
                        {datasets.value.length > 1 && (
                            <button type="button" class="dashboard-builder-icon-button danger" title="Supprimer" onClick={() => removeDataset(index)}>
                                <i class="material-symbols-outlined">delete</i>
                            </button>
                        )}
                    </div>

                    <div class="dashboard-builder-dataset-basics">
                        <label class="dashboard-builder-field">
                            <span>Nom affiché</span>
                            <input
                                maxlength={80}
                                value={dataset.label}
                                placeholder="Ex. Messages créés"
                                onInput={(event) => dataset.label = (event.target as HTMLInputElement).value}
                            />
                        </label>

                        <div class="dashboard-builder-field">
                            <span>{isHeatmap.value ? "Couleur des cases" : isRanking.value ? "Couleur des barres" : "Couleur"}</span>
                            <div class="dashboard-builder-color-picker">
                                {BuilderColors.map((color) => (
                                    <button
                                        type="button"
                                        class={{ selected: dataset.color === color }}
                                        style={{ "--series-color": color }}
                                        aria-label={`Choisir la couleur ${color}`}
                                        aria-pressed={dataset.color === color}
                                        onClick={() => dataset.color = color}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div class="dashboard-builder-expression-field">
                        <div class="dashboard-builder-expression-label">
                            <div>
                                <span>Expression LogsCord</span>
                                <small>La même syntaxe et les mêmes suggestions que dans la recherche de logs.</small>
                            </div>
                            {dataset.expression.trim() && !diagnostics.length && (
                                <span class="dashboard-builder-valid-expression">
                                    <i class="material-symbols-outlined">check_circle</i>
                                    Syntaxe prête
                                </span>
                            )}
                        </div>
                        <LogSearchEditor
                            key={index}
                            value={dataset.expression}
                            placeholder={`type == "message_create"`}
                            footer={false}
                            onupdate={(value) => dataset.expression = value}
                            onsubmit={refreshPreview}
                        />
                        {!dataset.expression.trim() && (
                            <div class="dashboard-builder-expression-help">
                                <i class="material-symbols-outlined">info</i>
                                Commence à écrire ou utilise <kbd>Ctrl</kbd> + <kbd>Espace</kbd> pour les suggestions.
                            </div>
                        )}
                        {diagnostics.length > 0 && (
                            <div class="dashboard-builder-expression-diagnostics">
                                {diagnostics.map((diagnostic) => (
                                    <span class={diagnostic.severity}>
                                        <i class="material-symbols-outlined">{diagnostic.severity === "error" ? "error" : "warning"}</i>
                                        {diagnostic.message}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <details class="dashboard-builder-advanced">
                        <summary>
                            <span>
                                <i class="material-symbols-outlined">tune</i>
                                Options avancées
                            </span>
                            <i class="material-symbols-outlined">expand_more</i>
                        </summary>

                        <div class="dashboard-builder-advanced-content">
                            <label class="dashboard-builder-field">
                                <span>Identifiant technique</span>
                                <input
                                    maxlength={80}
                                    value={dataset.name}
                                    onInput={(event) => dataset.name = (event.target as HTMLInputElement).value}
                                />
                            </label>

                            <label class="dashboard-builder-field dashboard-builder-checkbox">
                                <input
                                    type="checkbox"
                                    checked={dataset.drilldown.enabled}
                                    onChange={(event) => dataset.drilldown.enabled = (event.target as HTMLInputElement).checked}
                                />
                                <span>Activer le drilldown</span>
                            </label>

                            {dataset.drilldown.enabled && (
                                <>
                                    <label class="dashboard-builder-field">
                                        <span>Regrouper le drilldown par</span>
                                        <select
                                            value={dataset.drilldown.groupBy}
                                            onChange={(event) => dataset.drilldown.groupBy = (event.target as HTMLSelectElement).value as BuilderDataset["drilldown"]["groupBy"]}
                                        >
                                            <option value="executor">Executor</option>
                                            <option value="target">Target</option>
                                            <option value="channel">Channel</option>
                                        </select>
                                    </label>

                                    <div class="dashboard-builder-expression-field compact">
                                        <div class="dashboard-builder-expression-label">
                                            <div>
                                                <span>Expression de drilldown</span>
                                                <small>Optionnelle : vide, elle reprend l’expression principale.</small>
                                            </div>
                                        </div>
                                        <LogSearchEditor
                                            value={dataset.drilldown.expression}
                                            placeholder="Même expression que la mesure"
                                            compact
                                            footer={false}
                                            onupdate={(value) => dataset.drilldown.expression = value}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </details>
                </div>
            );
        }

        function renderDataStep() {
            const dataset = selectedDataset.value;

            return (
                <>
                    <div class="dashboard-builder-panel-heading with-action">
                        <div>
                            <span>Étape 2</span>
                            <h2>{usesSingleSeries.value ? `Définir ${isHeatmap.value ? "la série" : "la mesure"}` : `Définir les ${dataLabel.value.toLowerCase()}`}</h2>
                            <p>Chaque expression compte les logs qui correspondent à ses critères.</p>
                        </div>
                        {!usesSingleSeries.value && (
                            <button
                                type="button"
                                class="dashboard-option-button secondary"
                                disabled={datasets.value.length >= 8}
                                onClick={addDataset}
                            >
                                <i class="material-symbols-outlined">add</i>
                                Ajouter
                            </button>
                        )}
                    </div>

                    {isHeatmap.value && (
                        <div class="dashboard-builder-heatmap-notice">
                            <i class="material-symbols-outlined">schedule</i>
                            <span>
                                La heatmap utilise une seule série et affiche 4 semaines complètes, soit {heatmapOptions.value.cellCount} cases de 24 heures. L’intensité compare chaque jour aux autres jours affichés.
                            </span>
                        </div>
                    )}

                    {graphType.value === "donut" && (
                        <div class="dashboard-builder-heatmap-notice">
                            <i class="material-symbols-outlined">donut_large</i>
                            <span>
                                Chaque catégorie devient une part du donut. Pour une vraie répartition, utilise des expressions qui ne se chevauchent pas.
                            </span>
                        </div>
                    )}

                    {isRanking.value && (
                        <div class="dashboard-builder-ranking-options">
                            <div>
                                <i class="material-symbols-outlined">leaderboard</i>
                                <span>
                                    <strong>Classement dynamique</strong>
                                    <small>Une seule expression est regroupée automatiquement sur toute la période.</small>
                                </span>
                            </div>
                            <label class="dashboard-builder-field">
                                <span>Regrouper par</span>
                                <select
                                    value={rankingOptions.value.groupBy}
                                    onChange={(event) => {
                                        rankingOptions.value.groupBy = (event.target as HTMLSelectElement).value as DashboardRankingOptions["groupBy"];
                                        if (datasets.value[0])
                                            datasets.value[0].drilldown.groupBy = rankingOptions.value.groupBy;
                                    }}
                                >
                                    <option value="executor">Exécuteur</option>
                                    <option value="target">Cible</option>
                                    <option value="channel">Salon</option>
                                </select>
                            </label>
                            <label class="dashboard-builder-field">
                                <span>Nombre de résultats</span>
                                <select
                                    value={rankingOptions.value.limit}
                                    onChange={(event) => rankingOptions.value.limit = Number((event.target as HTMLSelectElement).value)}
                                >
                                    {[ 5, 10, 15, 20 ].map((limit) => (
                                        <option value={limit}>Top {limit}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}

                    <div class="dashboard-builder-series-list" role="tablist" aria-label={dataLabel.value}>
                        {datasets.value.map((entry, index) => (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={selectedDatasetIndex.value === index}
                                class={{ active: selectedDatasetIndex.value === index }}
                                onClick={() => selectedDatasetIndex.value = index}
                            >
                                <i style={{ background: entry.color }} />
                                <span>
                                    <strong>{entry.label || `${dataLabel.value.replace(/s$/, "")} ${index + 1}`}</strong>
                                    <small>{entry.expression.trim() || "Expression à définir"}</small>
                                </span>
                                <i class="material-symbols-outlined status">
                                    {entry.expression.trim() ? "check_circle" : "radio_button_unchecked"}
                                </i>
                            </button>
                        ))}
                    </div>

                    {dataset && renderDatasetEditor(dataset, selectedDatasetIndex.value)}
                </>
            );
        }

        function renderReviewStep() {
            return (
                <>
                    <div class="dashboard-builder-panel-heading">
                        <div>
                            <span>Étape 3</span>
                            <h2>Vérifier avant d’enregistrer</h2>
                            <p>Tout est prêt. Contrôle le rendu et les données utilisées.</p>
                        </div>
                    </div>

                    <div class="dashboard-builder-review-grid">
                        <div class="dashboard-builder-review-card">
                            <div class="dashboard-builder-review-icon">
                                <i class="material-symbols-outlined">{selectedGraphType.value.icon}</i>
                            </div>
                            <div>
                                <span>Graphique</span>
                                <strong>{label.value}</strong>
                                <small>{selectedGraphType.value.label}</small>
                            </div>
                            <button type="button" onClick={() => goToStep(1)}>Modifier</button>
                        </div>

                        <div class="dashboard-builder-review-card">
                            <div class="dashboard-builder-review-icon">
                                <i class="material-symbols-outlined">data_object</i>
                            </div>
                            <div>
                                <span>{dataLabel.value}</span>
                                <strong>{datasets.value.length} {dataLabel.value.toLowerCase()}</strong>
                                <small>Toutes les expressions sont renseignées</small>
                            </div>
                            <button type="button" onClick={() => goToStep(2)}>Modifier</button>
                        </div>
                    </div>

                    <div class="dashboard-builder-review-series">
                        <span>Expressions utilisées</span>
                        {datasets.value.map((dataset) => (
                            <div>
                                <i style={{ background: dataset.color }} />
                                <strong>{dataset.label}</strong>
                                <code>{dataset.expression}</code>
                                <span class="material-symbols-outlined">check_circle</span>
                            </div>
                        ))}
                    </div>
                </>
            );
        }

        function renderCurrentStep() {
            if (activeStep.value === 1)
                return renderAppearanceStep();

            if (activeStep.value === 2)
                return renderDataStep();

            return renderReviewStep();
        }

        return () => (
            <div id="app" class="dashboard-builder">
                <header class="dashboard-builder-header">
                    <router-link to="/" class="dashboard-builder-back" aria-label="Retour aux dashboards">
                        <i class="material-symbols-outlined">arrow_back</i>
                    </router-link>
                    <div class="dashboard-builder-header-copy">
                        <span>Dashboard studio</span>
                        <h1>{dashboardId.value ? "Modifier le graphique" : "Créer un graphique"}</h1>
                    </div>
                    <div class="dashboard-builder-header-status">
                        <i class="material-symbols-outlined">edit_note</i>
                        Modifications non enregistrées
                    </div>
                </header>

                <nav class="dashboard-builder-stepper" aria-label="Étapes de création">
                    {BuilderSteps.map((step) => (
                        <button
                            type="button"
                            class={{ active: activeStep.value === step.id, complete: activeStep.value > step.id }}
                            aria-current={activeStep.value === step.id ? "step" : undefined}
                            onClick={() => goToStep(step.id)}
                        >
                            <span class="dashboard-builder-step-number">
                                {activeStep.value > step.id ? <i class="material-symbols-outlined">check</i> : step.id}
                            </span>
                            <span>
                                <strong>{step.label}</strong>
                                <small>{step.description}</small>
                            </span>
                        </button>
                    ))}
                </nav>

                {formError.value && (
                    <div class="dashboard-builder-form-error" role="alert">
                        <i class="material-symbols-outlined">error</i>
                        <span>{formError.value}</span>
                        <button type="button" aria-label="Fermer" onClick={() => formError.value = null}>
                            <i class="material-symbols-outlined">close</i>
                        </button>
                    </div>
                )}

                <main class="dashboard-builder-workspace">
                    <section class="dashboard-builder-panel dashboard-builder-editor-panel">
                        <div class="dashboard-builder-step-content">
                            {renderCurrentStep()}
                        </div>

                        <footer class="dashboard-builder-footer">
                            <button
                                type="button"
                                class="dashboard-option-button secondary"
                                disabled={activeStep.value === 1}
                                onClick={() => goToStep((activeStep.value - 1) as BuilderStep)}
                            >
                                <i class="material-symbols-outlined">arrow_back</i>
                                Précédent
                            </button>

                            <span>Étape {activeStep.value} sur 3</span>

                            {activeStep.value < 3 ? (
                                <button type="button" class="dashboard-option-button primary" onClick={goNext}>
                                    Continuer
                                    <i class="material-symbols-outlined">arrow_forward</i>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    class="dashboard-option-button primary"
                                    disabled={saving.value || Boolean(configurationError.value)}
                                    onClick={saveDashboard}
                                >
                                    <i class="material-symbols-outlined">save</i>
                                    {saving.value ? "Enregistrement…" : dashboardId.value ? "Enregistrer" : "Créer le graphique"}
                                </button>
                            )}
                        </footer>
                    </section>

                    <aside class="dashboard-builder-panel dashboard-builder-preview">
                        <div class="dashboard-builder-preview-heading">
                            <div>
                                <span>Prévisualisation</span>
                                <small>{loading.value ? "Mise à jour…" : preview.value ? "Données réelles" : "Configuration"}</small>
                            </div>
                            <label>
                                <span>{isHeatmap.value ? "Cases" : "Période"}</span>
                                {isHeatmap.value ? (
                                    <span class="dashboard-builder-fixed-period">
                                        <i class="material-symbols-outlined">lock</i>
                                        {heatmapOptions.value.cellCount} × 24 h
                                    </span>
                                ) : (
                                    <select value={previewPeriod.value} onChange={(event) => previewPeriod.value = Number((event.target as HTMLSelectElement).value)}>
                                        {PreviewPeriods.map((period) => (
                                            <option value={period.duration}>{period.label}</option>
                                        ))}
                                    </select>
                                )}
                            </label>
                        </div>

                        <div class="dashboard-builder-preview-card">
                            <div class="dashboard-builder-preview-title">
                                <div>
                                    <strong>{label.value.trim() || "Graphique sans nom"}</strong>
                                    <span>{selectedGraphType.value.shortLabel}</span>
                                </div>
                                <button
                                    type="button"
                                    class={{ loading: loading.value }}
                                    title="Actualiser la prévisualisation"
                                    disabled={!canPreview.value || loading.value}
                                    onClick={refreshPreview}
                                >
                                    <i class="material-symbols-outlined">refresh</i>
                                </button>
                            </div>
                            {renderPreview()}
                        </div>

                        <div class="dashboard-builder-preview-note">
                            <i class="material-symbols-outlined">verified_user</i>
                            <span>La preview utilise uniquement les données réelles accessibles à ton compte.</span>
                        </div>
                    </aside>
                </main>
            </div>
        );
    },
});
